<template>
  <a-modal
    v-model:visible="visible"
    :footer="false"
    fullscreen
    :mask="true"
    :mask-closable="false"
    :closable="true"
    class="settings-modal"
    unmount-on-close
    @cancel="handleClose"
  >
    <template #title>
      <div class="modal-header">
        <span class="modal-title">{{ t('workspaceSettings.title') }}</span>
      </div>
    </template>

    <div class="settings-content">
      <div class="settings-container">
        <!-- 左侧导航 -->
        <div class="settings-sidebar">
          <div class="sidebar-menu">
            <div
              v-for="item in menuItems"
              :key="item.key"
              class="menu-item"
              :class="{ active: activeTab === item.key }"
              @click="activeTab = item.key"
            >
              <component
                :is="item.icon"
                v-if="activeTab !== item.key || !item.activeIcon"
              />
              <component :is="item.activeIcon" v-else />
              <span>{{ item.label }}</span>
            </div>
          </div>
        </div>

        <!-- 右侧内容区 -->
        <div class="settings-main">
          <div class="tab-content">
            <Members v-if="activeTab === 'members'" />
            <!-- <Resources v-if="activeTab === 'resources'" @close="handleClose" /> -->
            <ApiKey v-if="activeTab === 'api-key'" @close="handleClose" />
            <Identifiers v-if="activeTab === 'identifiers'" />
          </div>
        </div>
      </div>
    </div>
  </a-modal>
</template>

<script setup lang="ts">
  import { ref, computed, watch, h } from 'vue';
  import { useI18n } from 'vue-i18n';
  import {
    IconUser,
    IconFolder,
    IconBookmark,
  } from '@arco-design/web-vue/es/icon';
  import Members from './components/members.vue';
  import Resources from './components/resources.vue';
  import ApiKey from './components/ApiKey.vue';
  import selfIcon from '../selfIcon';
  import Identifiers from './components/identifiers.vue';

  const { t } = useI18n();

  const props = defineProps<{
    modelValue: boolean;
    defaultTab?: 'members' | 'resources' | 'api-key' | 'identifiers';
  }>();

  const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void;
  }>();

  const visible = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value),
  });

  const activeTab = ref(props.defaultTab || 'members');

  watch(
    () => props.defaultTab,
    (newTab) => {
      if (newTab) {
        activeTab.value = newTab;
      }
    }
  );

  watch(
    () => props.modelValue,
    (isVisible) => {
      if (isVisible && props.defaultTab) {
        activeTab.value = props.defaultTab;
      }
    }
  );

  const menuItems = computed(() => [
    {
      key: 'members',
      label: t('workspaceSettings.menu.members'),
      icon: IconUser,
    },
    // {
    //   key: 'resources',
    //   label: t('workspaceSettings.menu.resources'),
    //   icon: IconFolder,
    // },
    // API Key
    {
      key: 'api-key',
      label: t('workspaceSettings.menu.apiKey'),
      icon: () => h(selfIcon, { type: 'icon-key' }),
      activeIcon: () => h(selfIcon, { type: 'icon-key-copy' }),
    },
    {
      key: 'identifiers',
      label: t('workspaceSettings.menu.identifiers'),
      icon: IconBookmark,
    },
  ]);

  const handleClose = () => {
    visible.value = false;
  };
</script>

<style lang="less" scoped>
  .settings-modal {
    :deep(.arco-modal) {
      max-width: 100%;
      height: 100vh;
      margin: 0;
      padding: 0;
      border-radius: 0;
    }

    :deep(.arco-modal-header) {
      padding: 20px 24px;
      border-bottom: 1px solid var(--color-border-2);
    }

    :deep(.arco-modal-body) {
      padding: 0;
      height: calc(100vh - 60px);
      overflow: hidden;
    }

    .modal-header {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .modal-title {
        font-size: 18px;
        font-weight: 600;
        color: var(--color-text-1);
      }
    }
  }

  .settings-content {
    height: 100%;
    background: var(--color-bg-1);
  }

  .settings-container {
    display: flex;
    height: 100%;
  }

  .settings-sidebar {
    width: 240px;
    border-right: 1px solid var(--color-border-2);
    background: var(--color-bg-2);
    padding: 24px 0;

    .sidebar-menu {
      display: flex;
      flex-direction: column;
      gap: 4px;
      padding: 0 12px;

      .menu-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 16px;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.2s;
        color: var(--color-text-2);
        font-size: 14px;

        &:hover {
          background: var(--color-fill-2);
          color: var(--color-text-1);
        }

        &.active {
          background: var(--color-primary-light-1);
          color: var(--color-primary-6);
          font-weight: 500;
        }

        .arco-icon {
          font-size: 18px;
        }
      }
    }
  }

  .settings-main {
    flex: 1;
    overflow-y: auto;
    padding: 24px;
  }

  .tab-content {
    max-width: 1200px;
    margin: 0 auto;
  }
</style>
